/**
 * @fileoverview The account menu in the Dashboard header: avatar, first name,
 * a chevron, and a glass dropdown holding the two things an instructor checks
 * most often — how many AI minutes they have left, and what plan they are on
 * — with a way to act on each without leaving the page, and the way to
 * everything else.
 *
 * It replaced a plan pill beside an avatar that navigated straight to
 * Settings. Three things about the shape are decisions.
 *
 * **The menu answers, and then acts, in place.** Minutes left and the plan
 * name are read here, not on Settings, because "can I record this lesson?"
 * is asked from the Dashboard. Add minutes and Manage open the SAME checkout
 * and management bodies Settings renders (`TopUpCheckout`, `StripeCheckout`,
 * `StripeManagePanel`), in dialogs over this page, so there is one checkout
 * and one cancel flow however they are reached.
 *
 * **The bar follows the meter's rules (X10).** A monthly allowance gets a
 * bar; an exhausted account gets a full one; a one-time balance gets a
 * figure and no bar, because a bar promises a refill that a one-time grant
 * does not have. The dropdown and the Settings meter must never disagree
 * about the same minutes.
 *
 * **Manage is one control for every plan state.** Free and unseated opens
 * the ladder; a Stripe plan opens cycle, next date and cancel; an App Store
 * plan says where it is managed; a seat or a founding pin says who provides
 * it. One button beside the plan name, whatever the answer.
 *
 * Hand-rolled rather than a Radix menu: the content is a meter and two
 * buttons, not a list of commands, and the dependency is not installed.
 * Outside-click and Escape close it; the trigger reports `aria-expanded`.
 */

import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Plus } from "lucide-react";

import StripeManagePanel, { hasAppleSubscription, hasStripeSubscription, useStripeDetails } from "@/components/settings/StripeManagePanel";
import StripeCheckout from "@/components/StripeCheckout";
import TopUpCheckout from "@/components/TopUpCheckout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import useUsage from "@/hooks/useUsage";
import { subscriptionsEnabled } from "@/lib/featureFlags";
import { formatComplimentaryUntil, isFoundingMember } from "@/lib/founding";
import { accountTier, bestOrgSeat, hasLessonAi, isPaidTier, tierDisplayName } from "@/lib/tiers";
import { buildUsageModel, formatMinutes } from "@/lib/usageMeter";
import { avatarInitial, firstName } from "@/lib/userName";

const EYEBROW = "font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]";
const DIALOG_CLS = "bg-[var(--studio-raised)] border-[var(--studio-divider)] max-h-[92vh] overflow-y-auto";

/** The thin bar: monthly progress, or full when nothing is left. */
function MinutesBar({ ctx }) {
  if (!ctx) return null;
  const exhausted = !ctx.monthly && ctx.pools.length === 0;
  if (!ctx.monthly && !exhausted) return null;
  const pct = ctx.monthly ? Math.min((ctx.monthly.usedMinutes / ctx.monthly.limitMinutes) * 100, 100) : 100;
  const spent = pct >= 100;
  return (
    <div
      className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[var(--studio-interactive)]"
      data-testid={exhausted ? "menu-minutes-exhausted" : "menu-minutes-bar"}
      role="img"
      aria-label={exhausted ? "No recording minutes left" : `${Math.round(pct)}% of monthly minutes used`}
    >
      <div
        className={`h-full rounded-full ${spent ? "bg-[var(--state-danger)]" : "bg-[var(--accent-yellow)]"}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/**
 * The body of the plan dialog for an account that is NOT buying: says where
 * the plan comes from and, for a Stripe plan, lets them manage it.
 */
function ManagePlanBody({ user, tier, seat }) {
  const stripeSub = hasStripeSubscription(user);
  const { details, loading, setDetails } = useStripeDetails(stripeSub);
  const until = formatComplimentaryUntil(user);

  let note;
  if (seat) {
    note = `Provided by ${seat.org_name}. Your organization manages this plan for you.`;
  } else if (isFoundingMember(user) && until) {
    note = `Complimentary through ${until} as a Founding Ensemble member. Your badge is permanent.`;
  } else if (hasAppleSubscription(user)) {
    note = "Billed through the App Store.";
  } else if (stripeSub) {
    note = null;
  } else {
    note = "Manage your plan from Settings.";
  }

  return (
    <div data-testid="manage-plan-body">
      <p className={EYEBROW}>Current plan</p>
      <p className="mt-1 font-sans text-[20px] font-semibold text-[var(--text-primary)]">{tierDisplayName(tier)}</p>
      {note && <p className="mt-2 font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">{note}</p>}
      {hasAppleSubscription(user) && (
        <a
          href="https://apps.apple.com/account/subscriptions"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1 font-sans text-[13px] text-[var(--accent-yellow-text)] hover:underline"
        >
          Manage on iOS <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {stripeSub && <StripeManagePanel user={user} details={details} loading={loading} onDetails={setDetails} />}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.user The instructor from /auth/me.
 */
export default function AccountMenu({ user }) {
  const subsEnabled = subscriptionsEnabled(user);
  const { usage, loading } = useUsage(subsEnabled);
  const [open, setOpen] = useState(false);
  // Which dialog is up: "topup" | "plan" | null. Rendered OUTSIDE the panel,
  // so closing the menu never unmounts a checkout mid-payment.
  const [dialog, setDialog] = useState(null);
  // Reported by StripeCheckout so the popup shrinks to the payment form.
  const [checkoutStep, setCheckoutStep] = useState("select");
  const root = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (root.current && !root.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name = firstName(user) || "Account";
  const tier = accountTier(user);
  const seat = bestOrgSeat(user);
  // An org seat covers this account — no upsell; a personal purchase stays
  // possible from Settings if they want to outrank the seat tier.
  const canUpgrade = subsEnabled && !isPaidTier(tier) && !seat;

  const model = usage ? buildUsageModel(usage) : null;
  const personal = model?.contexts.find((c) => c.key === "personal") || model?.contexts[0] || null;
  const canTopUp = !!usage && hasLessonAi(usage.tier);

  const openDialog = (which) => {
    setOpen(false);
    setDialog(which);
  };

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${name}: account menu`}
        data-testid="account-menu"
        className="flex items-center gap-2.5 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-[var(--studio-raised)]"
      >
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-10 w-10 rounded-full" />
        ) : (
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-interactive)] font-sans font-semibold text-[var(--accent-yellow-text)]">
            {avatarInitial(user)}
          </span>
        )}
        <span className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{name}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 text-[var(--text-tertiary)] transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        // Glass: the page shows through, so the menu reads as a layer over
        // the dashboard rather than a second card beside it.
        <div
          role="menu"
          aria-label="Account"
          data-testid="account-menu-panel"
          className="absolute right-0 top-full z-30 mt-2 w-[300px] rounded-2xl border border-[var(--studio-divider-strong)] bg-[var(--studio-glass)] p-2 shadow-[var(--studio-shadow)] backdrop-blur-xl backdrop-saturate-150"
        >
          {subsEnabled && (
            <>
              <div className="px-3 pb-3 pt-2" data-testid="menu-minutes">
                <div className="flex items-baseline justify-between gap-3">
                  <span className={EYEBROW}>AI minutes</span>
                  <span className="font-mono text-[13px] text-[var(--text-primary)]" data-testid="menu-minutes-left">
                    {loading ? "…" : personal ? `${formatMinutes(personal.totalMinutes)} left` : "—"}
                  </span>
                </div>
                <MinutesBar ctx={personal} />
                {personal && !personal.monthly && personal.pools.length > 0 && (
                  <p className="mt-1.5 font-sans text-[12px] text-[var(--text-tertiary)]">
                    {personal.pools.map((p) => `${formatMinutes(p.minutes)} ${p.label.toLowerCase()}`).join(" · ")}
                  </p>
                )}
                {canTopUp && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => openDialog("topup")}
                    data-testid="menu-add-minutes"
                    className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-[var(--accent-yellow)] px-4 py-2 font-sans text-[13px] font-semibold text-[var(--text-inverse)] transition-transform active:scale-95"
                  >
                    <Plus className="h-3.5 w-3.5" strokeWidth={2.5} /> Add minutes
                  </button>
                )}
              </div>

              <div className="mx-1 border-t border-[var(--studio-divider)]" />

              <div className="px-3 pb-3 pt-3" data-testid="menu-plan">
                <span className={EYEBROW}>Current plan</span>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <p className="font-sans text-[16px] font-semibold text-[var(--text-primary)]" data-testid="dashboard-plan">
                    {tierDisplayName(tier)}
                  </p>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => openDialog("plan")}
                    data-testid="menu-manage-plan"
                    className="rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3.5 py-1 font-sans text-[12px] font-semibold text-[var(--text-primary)] transition-colors hover:border-[var(--accent-yellow-border-strong)] hover:text-[var(--accent-yellow-text)]"
                  >
                    Manage
                  </button>
                </div>
                {seat && (
                  <p className="mt-0.5 font-sans text-[12px] text-[var(--text-tertiary)]">Provided by {seat.org_name}</p>
                )}
              </div>

              <div className="mx-1 border-t border-[var(--studio-divider)]" />
            </>
          )}

          <a
            href="/settings"
            role="menuitem"
            data-testid="menu-all-settings"
            onClick={() => setOpen(false)}
            className="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left font-sans text-[14px] text-[var(--text-primary)] transition-colors hover:bg-[var(--studio-overlay)]"
          >
            All settings
            <ChevronRight className="h-4 w-4 text-[var(--text-tertiary)]" strokeWidth={1.8} />
          </a>
        </div>
      )}

      {/* Add minutes — the same top-up body Settings renders inline, here in
          a dialog that owns the title and the close control. */}
      <Dialog open={dialog === "topup"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className={`${DIALOG_CLS} sm:max-w-md`} data-testid="topup-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-[var(--text-primary)]">Add recording minutes</DialogTitle>
          </DialogHeader>
          {dialog === "topup" && <TopUpCheckout onClose={() => setDialog(null)} showHeader={false} />}
        </DialogContent>
      </Dialog>

      {/* Manage — the ladder for an account that can buy, otherwise where the
          plan comes from and how to change it. Mounted only while open, so
          no Stripe session is created behind a closed dialog. */}
      <Dialog
        open={dialog === "plan"}
        onOpenChange={(o) => {
          if (!o) {
            setDialog(null);
            setCheckoutStep("select");
          }
        }}
      >
        {/* Sized to the STEP — see the same note on SettingsPage. The plan grid
            needs 960px; Stripe's form is a narrow centred column that would sit
            marooned in it. */}
        <DialogContent
          className={`${DIALOG_CLS} ${
            canUpgrade
              ? `w-[calc(100vw-2rem)] p-6 transition-[max-width] duration-200 md:p-8 ${
                  checkoutStep === "select" ? "sm:max-w-[960px]" : "sm:max-w-[540px]"
                }`
              : "sm:max-w-md"
          }`}
          data-testid="plan-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-[var(--text-primary)]">
              {canUpgrade ? "Upgrade your plan" : "Your plan"}
            </DialogTitle>
          </DialogHeader>
          {dialog === "plan" &&
            (canUpgrade ? (
              <StripeCheckout onStepChange={setCheckoutStep} />
            ) : (
              <ManagePlanBody user={user} tier={tier} seat={seat} />
            ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
